import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../utils/api';

const HeroSection = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useState({
    destination: '',
    category: '',
    duration: ''
  });
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const inputRef = useRef(null);

  // Fetch categories from the backend
  useEffect(() => {
    const fetchCategories = async () => {
      setLoadingCategories(true);
      try {
        const response = await api.get('/api/v1/categories');
        if (response.data && response.data.data) {
          setCategories(response.data.data);
        } else {
          console.error('Invalid category data format:', response.data);
          setCategories([]);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        setCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  const durations = [
    { value: '1-3', label: '1-3 days' },
    { value: '4-7', label: '4-7 days' },
    { value: '8-14', label: '8-14 days' },
    { value: '15+', label: '15+ days' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    console.log(`Input changed: ${name} = ${value}`); // Debug log
    
    setSearchParams(prev => ({
      ...prev,
      [name]: value
    }));

    // If destination field is being typed in, fetch suggestions
    if (name === 'destination' && value.trim().length > 2) {
      fetchSuggestions(value);
    } else if (name === 'destination') {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Fetch destination suggestions as user types
  const fetchSuggestions = async (query) => {
    if (query.trim().length < 3) return;
    
    setIsLoading(true);
    try {
      // Fetch real locations from the backend API
      const response = await api.get('/api/v1/locations/search', {
        params: { query }
      });
      
      // Check if the response has the expected structure
      if (response.data && Array.isArray(response.data.data)) {
        setSuggestions(response.data.data.map(location => ({
          id: location.id,
          name: location.name,
          // Include additional location details if available
          region: location.region || '',
          country: location.country || ''
        })));
      } else {
        console.warn('Unexpected location data format:', response.data);
        setSuggestions([]);
      }
      
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error fetching location suggestions:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle suggestion selection
  const handleSuggestionClick = (suggestion) => {
    // Use the location name for display in the input field
    setSearchParams(prev => ({
      ...prev,
      destination: suggestion.name,
      // Store the location ID if needed for the backend search
      locationId: suggestion.id
    }));
    setShowSuggestions(false);
    
    // Auto-navigate to search results when suggestion is selected
    const queryParams = new URLSearchParams();
    queryParams.append('search', suggestion.name);
    // If you want to search by location ID instead of name
    queryParams.append('location_id', suggestion.id);
    if (searchParams.category) {
      queryParams.append('category', searchParams.category);
    }
    if (searchParams.duration) {
      queryParams.append('duration', searchParams.duration);
    }
    
    navigate({
      pathname: '/tours',
      search: queryParams.toString()
    });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    
    // Build query string from search parameters
    const queryParams = new URLSearchParams();
    
    if (searchParams.destination) {
      queryParams.append('search', searchParams.destination);
    }
    
    if (searchParams.category) {
      queryParams.append('category', searchParams.category);
    }
    
    if (searchParams.duration) {
      queryParams.append('duration', searchParams.duration);
    }
    
    // Navigate to tours page with search parameters
    navigate({
      pathname: '/tours',
      search: queryParams.toString()
    });
  };

  // Focus the input field when component mounts
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  return (
    <section className="relative h-hero">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{ 
          backgroundImage: "url('/images/hero-bg.jpeg')",
          backgroundPosition: 'center',
          backgroundSize: 'cover'
        }}
      >
        {/* Overlay with gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/50"></div>
      </div>
      
      {/* Hero Content */}
      <div className="relative z-10 h-full flex flex-col justify-between items-center text-white px-4 sm:px-6 lg:px-8 py-24">
        {/* Main heading content - pushed to top half */}
        <div className="text-center max-w-4xl mx-auto mt-12">
          <div className="animate-fadeIn">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 leading-tight text-shadow-lg">
              Discover the Magic of <span className="text-primary">Wildlife</span>
            </h1>
            <p className="text-base md:text-lg mb-6 max-w-2xl mx-auto text-shadow-sm">
              Experience unforgettable adventures with our premium safari tours and wildlife experiences
            </p>
          </div>
        </div>
        
        {/* Search Form - pushed to bottom */}
        <div className="w-full max-w-5xl mx-auto mt-auto mb-8 transform translate-y-8">
          <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-2xl p-5 md:p-6 border border-white/20">
            <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-4 gap-5">
              <div className="md:col-span-1 relative">
                <label htmlFor="destination" className="block text-gray-700 text-sm font-semibold mb-1">
                  Destination
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </span>
                  <input
                    type="text"
                    id="destination"
                    name="destination"
                    placeholder="Where to?"
                    className="form-input w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-gray-800 shadow-sm"
                    value={searchParams.destination}
                    onChange={handleInputChange}
                    onFocus={() => searchParams.destination.length >= 3 && setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    autoComplete="off"
                    ref={inputRef}
                  />
                </div>
                
                {/* Suggestions dropdown */}
                {showSuggestions && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-xl max-h-60 overflow-auto">
                    {isLoading ? (
                      <div className="p-3 text-gray-500 text-center flex items-center justify-center">
                        <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Loading locations...
                      </div>
                    ) : suggestions.length > 0 ? (
                      suggestions.map((suggestion) => (
                        <div
                          key={suggestion.id}
                          className="p-3 hover:bg-gray-100 cursor-pointer text-gray-800 text-left border-b border-gray-100 last:border-b-0"
                          onMouseDown={() => handleSuggestionClick(suggestion)}
                        >
                          <div className="flex items-start">
                            <span className="text-primary mr-2">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              </svg>
                            </span>
                            <div>
                              <div className="font-medium">{suggestion.name}</div>
                              {(suggestion.region || suggestion.country) && (
                                <div className="text-xs text-gray-500">
                                  {[suggestion.region, suggestion.country].filter(Boolean).join(', ')}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : searchParams.destination.length >= 3 ? (
                      <div className="p-3 text-gray-500 text-center">
                        No locations found matching "{searchParams.destination}"
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
              
              <div className="md:col-span-1">
                <label htmlFor="category" className="block text-gray-700 text-sm font-semibold mb-1">
                  Category
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </span>
                  <select
                    id="category"
                    name="category"
                    className="form-select w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-gray-800 shadow-sm appearance-none"
                    value={searchParams.category}
                    onChange={handleInputChange}
                  >
                    <option value="">All Categories</option>
                    {loadingCategories ? (
                      <option disabled>Loading categories...</option>
                    ) : categories.length > 0 ? (
                      categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))
                    ) : (
                      <option disabled>No categories available</option>
                    )}
                  </select>
                  <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none text-gray-500">
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </span>
                </div>
              </div>
              
              <div className="md:col-span-1">
                <label htmlFor="duration" className="block text-gray-700 text-sm font-semibold mb-1">
                  Duration
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </span>
                  <select
                    id="duration"
                    name="duration"
                    className="form-select w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-gray-800 shadow-sm appearance-none"
                    value={searchParams.duration}
                    onChange={handleInputChange}
                  >
                    <option value="">Any Duration</option>
                    {durations.map((duration, index) => (
                      <option key={index} value={duration.value}>{duration.label}</option>
                    ))}
                  </select>
                  <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none text-gray-500">
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </span>
                </div>
              </div>
              
              <div className="md:col-span-1">
                <label className="invisible block text-gray-700 text-sm font-semibold mb-1">
                  Search
                </label>
                <button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary-dark text-white font-bold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:-translate-y-1 hover:shadow-lg flex items-center justify-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  Search Tours
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
